import React, { useEffect, useRef, useState } from "react";
import {
  Paintbrush, Image as ImageIcon, Save, X, Plus, Trash2, Edit3, Shield, Users as UsersIcon,
  CheckCircle2, Palette, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/auth.jsx";
import { BUILTIN_ROLE_FEATURE_DEFAULTS, ALL_FEATURE_KEYS } from "@/pages/navConfigs";

/* ---------------- Branding Defaults Panel ---------------- */
export function BrandingDefaultsPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const logoRef = useRef(null);
  const wmRef = useRef(null);

  useEffect(() => {
    api.get("/admin/branding-defaults").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/branding-defaults", { ...cfg, ...patch });
      setCfg(r.data);
      toast.success("Branding defaults saved · new Docs / LOAs will use this");
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const upload = (key) => (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => save({ [key]: reader.result });
    reader.readAsDataURL(file);
  };

  if (!cfg) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="dashboard-card p-6 space-y-5" data-testid="branding-defaults-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-black text-lg flex items-center gap-2"><Paintbrush className="w-5 h-5 text-fuchsia-500" /> Branding Defaults</h3>
          <p className="text-xs text-slate-500 mt-1">Logo, header, sub-header, footer and watermark used by every new Document and Letter of Appointment. Body text stays per-author.</p>
        </div>
        <button onClick={() => save()} disabled={busy} className="btn-primary text-xs" data-testid="branding-save"><Save className="w-3.5 h-3.5" /> Save</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Logo */}
        <Field label="Logo">
          {cfg.logo_url ? (
            <div className="flex items-center gap-3">
              <img src={cfg.logo_url} alt="logo" className="h-16 rounded-xl border border-slate-100 object-contain" />
              <button onClick={() => save({ logo_url: null })} className="text-xs text-rose-500">Remove</button>
            </div>
          ) : (
            <label className="btn-ghost text-xs cursor-pointer" data-testid="branding-logo-upload">
              <ImageIcon className="w-3.5 h-3.5" /> Upload logo
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload("logo_url")(e.target.files?.[0])} />
            </label>
          )}
        </Field>

        {/* Watermark image */}
        <Field label="Watermark image (overrides text)">
          {cfg.watermark_image_url ? (
            <div className="flex items-center gap-3">
              <img src={cfg.watermark_image_url} alt="wm" className="h-16 rounded-xl border border-slate-100 opacity-60 object-contain" />
              <button onClick={() => save({ watermark_image_url: null })} className="text-xs text-rose-500">Remove</button>
            </div>
          ) : (
            <label className="btn-ghost text-xs cursor-pointer" data-testid="branding-wm-image-upload">
              <ImageIcon className="w-3.5 h-3.5" /> Upload image
              <input ref={wmRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload("watermark_image_url")(e.target.files?.[0])} />
            </label>
          )}
        </Field>

        <Field label="Header text">
          <input className="input-pill text-sm" value={cfg.header_text || ""} onChange={(e) => setCfg({ ...cfg, header_text: e.target.value })} onBlur={() => save({ header_text: cfg.header_text })} data-testid="branding-header-text" />
        </Field>
        <Field label="Sub-header">
          <input className="input-pill text-sm" value={cfg.sub_header_text || ""} onChange={(e) => setCfg({ ...cfg, sub_header_text: e.target.value })} onBlur={() => save({ sub_header_text: cfg.sub_header_text })} data-testid="branding-sub-header" />
        </Field>
        <Field label="Footer text">
          <input className="input-pill text-sm" value={cfg.footer_text || ""} onChange={(e) => setCfg({ ...cfg, footer_text: e.target.value })} onBlur={() => save({ footer_text: cfg.footer_text })} data-testid="branding-footer" />
        </Field>
        <Field label="Watermark text">
          <input className="input-pill text-sm" placeholder="e.g. OFFICIAL" value={cfg.watermark_text || ""} onChange={(e) => setCfg({ ...cfg, watermark_text: e.target.value })} onBlur={() => save({ watermark_text: cfg.watermark_text })} data-testid="branding-watermark-text" />
        </Field>
      </div>
    </div>
  );
}

/* ---------------- Custom Role Creator ---------------- */
export function CustomRolesPanel() {
  const [roles, setRoles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [builtInEdit, setBuiltInEdit] = useState(null);

  const load = async () => {
    const [r, f, o] = await Promise.all([
      api.get("/admin/custom-roles"),
      api.get("/admin/features"),
      api.get("/admin/role-overrides").catch(() => ({ data: {} })),
    ]);
    setRoles(r.data || []);
    setFeatures(f.data?.features || []);
    setOverrides(o.data || {});
  };

  useEffect(() => { load(); }, []);

  const del = async (rid) => {
    if (!window.confirm("Delete this custom role? Users with this role must be reassigned first.")) return;
    try {
      await api.delete(`/admin/custom-roles/${rid}`);
      toast.success("Role deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const builtInRoles = Object.keys(BUILTIN_ROLE_FEATURE_DEFAULTS).filter((k) => k !== "admin"); // Admin's own sidebar is not overrideable

  return (
    <div className="dashboard-card p-6 space-y-6" data-testid="custom-roles-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-black text-lg flex items-center gap-2"><Shield className="w-5 h-5 text-indigo-500" /> Roles & Sidebar Features</h3>
          <p className="text-xs text-slate-500 mt-1">Configure what each role can see in their dashboard. Built-in roles have sensible defaults you can override; custom roles are fully user-defined.</p>
        </div>
        <button onClick={() => { setEdit(null); setOpen(true); }} className="btn-primary text-xs" data-testid="role-add-btn"><Plus className="w-3.5 h-3.5" /> New custom role</button>
      </div>

      {/* Built-in roles */}
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Built-in Roles ({builtInRoles.length})</div>
        <div className="grid md:grid-cols-2 gap-3">
          {builtInRoles.map((rk) => {
            const label = ROLE_LABELS[rk] || rk;
            const isOverridden = !!overrides[rk];
            const currentFeatures = isOverridden ? overrides[rk].features : BUILTIN_ROLE_FEATURE_DEFAULTS[rk];
            return (
              <div key={rk} className="border border-slate-200 rounded-xl p-3" data-testid={`builtin-role-${rk}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold flex items-center gap-2">
                      {label}
                      {isOverridden && <span className="text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Overridden</span>}
                    </div>
                    <div className="text-[10px] text-slate-400">slug: {rk}</div>
                  </div>
                  <button onClick={() => setBuiltInEdit({ role: rk, label, features: new Set(currentFeatures) })} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid={`edit-builtin-${rk}`}><Edit3 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {currentFeatures.map((f) => <span key={f} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{f}</span>)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom roles */}
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Custom Roles ({roles.length})</div>
        {roles.length === 0 ? (
          <div className="text-slate-400 text-sm text-center py-4">No custom roles yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {roles.map((r) => (
              <div key={r.role_id} className="border border-slate-200 rounded-xl p-3" data-testid={`custom-role-${r.role_key}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold flex items-center gap-2">
                      {r.label}
                      <span className="inline-flex items-center gap-0.5 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white shadow-sm" title="Custom role created by Admin">
                        <Sparkles className="w-2.5 h-2.5" /> Custom
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400">slug: {r.role_key} · scope: {r.meeting_scope}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEdit(r); setOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-100"><Edit3 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(r.role_id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full">profile</span>
                  <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full">meetings</span>
                  {(r.features || []).map((f) => (
                    <span key={f} className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-bold">{f}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && <RoleEditor features={features} role={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {builtInEdit && <BuiltInRoleEditor state={builtInEdit} onClose={() => setBuiltInEdit(null)} onSaved={() => { setBuiltInEdit(null); load(); }} />}
    </div>
  );
}

function BuiltInRoleEditor({ state, onClose, onSaved }) {
  const [selected, setSelected] = useState(state.features);
  const [busy, setBusy] = useState(false);
  const toggle = (f) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(f)) next.delete(f); else next.add(f);
    return next;
  });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/role-overrides/${state.role}`, { features: Array.from(selected) });
      toast.success(`Sidebar for ${state.label} updated`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const resetDefault = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/role-overrides/${state.role}`);
      toast.success("Restored default sidebar");
      onSaved();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative" data-testid="builtin-role-editor">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-black text-lg">Edit sidebar — {state.label}</h3>
            <p className="text-xs text-slate-500">Toggle which pages appear in this role's sidebar.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {ALL_FEATURE_KEYS.map((f) => {
              const active = selected.has(f);
              return (
                <button
                  key={f}
                  onClick={() => toggle(f)}
                  data-testid={`toggle-feature-${f}`}
                  className={`p-2.5 rounded-xl text-xs font-semibold text-left border transition ${active ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "bg-slate-50 border-transparent text-slate-500 hover:bg-slate-100"}`}
                >
                  {active ? "✓ " : ""}{f}
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center gap-2">
          <button onClick={resetDefault} disabled={busy} className="btn-ghost text-xs">Restore default</button>
          <div className="flex-1"></div>
          <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary text-xs" data-testid="save-builtin-features">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}


function RoleEditor({ features, role, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    label: role?.label || "",
    role_key: role?.role_key || "",
    icon: role?.icon || "user",
    accent: role?.accent || "indigo",
    features: new Set(role?.features || []),
    meeting_scope: role?.meeting_scope || "executive",
  }));
  const [busy, setBusy] = useState(false);

  const toggle = (k) => setForm((f) => {
    const fs = new Set(f.features);
    if (fs.has(k)) fs.delete(k); else fs.add(k);
    return { ...f, features: fs };
  });

  const submit = async () => {
    if (!form.label.trim()) return toast.error("Label required");
    setBusy(true);
    try {
      const payload = { ...form, features: Array.from(form.features) };
      if (!role) {
        if (!payload.role_key) payload.role_key = payload.label.toLowerCase().replace(/\s+/g, "_");
        await api.post("/admin/custom-roles", payload);
        toast.success("Role created");
      } else {
        await api.patch(`/admin/custom-roles/${role.role_id}`, payload);
        toast.success("Role updated");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto relative" data-testid="role-editor-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-black text-lg">{role ? "Edit role" : "Create custom role"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="role-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role name">
              <input className="input-pill" placeholder="e.g. Outreach Lead" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="role-label" />
            </Field>
            <Field label="Slug (internal)">
              <input className="input-pill" disabled={!!role} placeholder="outreach_lead" value={form.role_key} onChange={(e) => setForm({ ...form, role_key: e.target.value.toLowerCase().replace(/\s+/g, "_") })} data-testid="role-key" />
            </Field>
          </div>

          <Field label="Meeting scope">
            <div className="grid grid-cols-2 gap-2">
              {["executive", "manager"].map((s) => (
                <button key={s} onClick={() => setForm({ ...form, meeting_scope: s })} className={`p-3 rounded-xl text-sm font-semibold ${form.meeting_scope === s ? "bg-indigo-50 border-2 border-indigo-300 text-indigo-700" : "bg-slate-50 border-2 border-transparent text-slate-500"}`} data-testid={`role-scope-${s}`}>
                  {s === "manager" ? "Manager / Head — region-wide meetings" : "Executive — same-city peers only"}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Features (Profile + Meetings always included)">
            <div className="grid grid-cols-2 gap-2">
              {features.map((f) => (
                <label key={f.key} className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer ${form.features.has(f.key) ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`} data-testid={`role-feature-${f.key}`}>
                  <input type="checkbox" checked={form.features.has(f.key)} onChange={() => toggle(f.key)} />
                  <span className="text-sm">{f.label}</span>
                  {form.features.has(f.key) && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 ml-auto" />}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="role-save"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Edit User Modal ---------------- */
const BUILTIN_ROLES = ["admin", "regional_pm", "project_manager", "social_media_manager", "social_media_executive", "head_media_comm", "media_comm_executive", "head_academics", "seo", "school_principal", "designer", "accountant"];
const MANAGER_ROLES = ["regional_pm", "social_media_manager", "head_media_comm", "head_academics", "seo"];
const EXECUTIVE_PARENT_ROLE = {
  project_manager: "regional_pm",
  social_media_executive: "social_media_manager",
  media_comm_executive: "head_media_comm",
};

export function EditUserModal({ user, allUsers = [], customRoles = [], onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: user.name || "",
    email: user.email || "",
    role: user.role || "",
    city: user.city || "",
    region: user.region || "",
    parent_id: user.parent_id || "",
    member_id: user.member_id || "",
    end_tenure_date: user.end_tenure_date || "",
    contact_number: user.contact_number || "",
    active: user.active !== false,
  }));
  const [busy, setBusy] = useState(false);

  const COUNTRY = ["regional_pm", "social_media_manager", "head_media_comm", "head_academics", "seo"];
  const admins = allUsers.filter((u) => u.role === "admin");
  const customRoleKeys = customRoles.map((c) => c.role_key);

  // Reports-to filtering identical to the Create User modal:
  // Managers/Heads → Admin; Executives → their team Manager; Custom → Admin.
  const parentCandidates = (() => {
    const role = form.role;
    if (!role || role === "school_principal" || role === "admin") return [];
    if (MANAGER_ROLES.includes(role)) return admins;
    if (EXECUTIVE_PARENT_ROLE[role]) {
      const mgrRole = EXECUTIVE_PARENT_ROLE[role];
      return allUsers.filter((u) => u.role === mgrRole);
    }
    if (customRoleKeys.includes(role)) return admins;  // custom roles default to Admin
    return [];
  })();
  const parentLabel = MANAGER_ROLES.includes(form.role) || customRoleKeys.includes(form.role)
    ? "Admin"
    : (ROLE_LABELS[EXECUTIVE_PARENT_ROLE[form.role]] || "Supervisor");

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (COUNTRY.includes(payload.role)) payload.city = null;
      const r = await api.patch(`/admin/users/${user.user_id}`, payload);
      toast.success("User updated");
      onSaved(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto relative" data-testid="edit-user-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50">
          <h3 className="font-display font-black text-lg flex items-center gap-2"><Edit3 className="w-5 h-5 text-orange-600" /> Edit User</h3>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg" data-testid="eu-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Name"><input className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="eu-name" /></Field>
          <Field label="Email"><input type="email" className="input-pill" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="eu-email" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <select
                className="input-pill"
                value={form.role}
                onChange={(e) => {
                  const role = e.target.value;
                  // Clear parent when role changes — different supervisor pool
                  setForm((f) => ({ ...f, role, parent_id: "" }));
                }}
                data-testid="eu-role"
              >
                <optgroup label="Built-in roles">
                  {BUILTIN_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                </optgroup>
                {customRoles.length > 0 && (
                  <optgroup label="Custom roles">
                    {customRoles.map((c) => <option key={c.role_key} value={c.role_key}>{c.label}</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
            <Field label="Member ID">
              <input className="input-pill" value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} data-testid="eu-member-id" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={COUNTRY.includes(form.role) ? "Region (country-scoped)" : "City"}>
              <input className="input-pill" value={COUNTRY.includes(form.role) ? form.region : form.city} onChange={(e) => COUNTRY.includes(form.role) ? setForm({ ...form, region: e.target.value }) : setForm({ ...form, city: e.target.value })} data-testid="eu-location" />
            </Field>
            <Field label="End of Tenure">
              <input type="date" className="input-pill" value={form.end_tenure_date || ""} onChange={(e) => setForm({ ...form, end_tenure_date: e.target.value })} data-testid="eu-end-tenure" />
            </Field>
          </div>
          {parentCandidates.length > 0 && form.role !== "school_principal" && (
            <Field label={`Reports to · ${parentLabel}`}>
              <select className="input-pill" value={form.parent_id || ""} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="eu-parent">
                <option value="">— None —</option>
                {parentCandidates.filter((u) => u.user_id !== user.user_id).map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Contact number">
            <input className="input-pill" value={form.contact_number || ""} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} data-testid="eu-contact" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="eu-active" />
            <span>Active (uncheck to deactivate without deleting)</span>
          </label>
          <div className="text-[10px] text-slate-400">Password reset is handled separately via the &quot;Reset password&quot; button in the user list.</div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="eu-save"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-wide">{label}</label>
      {children}
    </div>
  );
}
